import React, { useState, useEffect, useCallback } from 'react';
import {
    RefreshCw, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    Calendar, User, Clock, FileText, ArrowUp, ArrowDown, ArrowUpDown,
    Loader2, LogIn, LogOut, AlertCircle, X, Copy, Check, HardDrive,
    Terminal, Tag
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import baseapi from '../../services/baseapi';
import { showError } from '../../utils/toast';
import { ItemsPerPageDropdown } from '../../components/ItemsPerPageDropdown ';
import { useSearchParams } from 'react-router-dom';
import { useClientLevel } from '../../hooks/useClientLevel';

export interface NVRLogItem {
    id: number;
    created: string;
    updated: string;
    is_deleted: boolean;
    organization_id: number;
    client_id: number;
    asset_id: number;
    log_time: string;
    log_type: string;
    log_user: string;
    detail: string | Record<string, any>;
    [key: string]: any;
}

// --- Helper Functions ---
const parseLogDetail = (detail: string | Record<string, any>): Record<string, any> => {
    if (!detail) return {};
    if (typeof detail === 'object') return detail;
    try {
        const parsed = JSON.parse(detail);
        if (typeof parsed === 'string') {
            try {
                return JSON.parse(parsed);
            } catch {
                return { Message: parsed };
            }
        }
        return parsed && typeof parsed === 'object' ? parsed : { Detail: String(parsed) };
    } catch {
        return { Detail: detail };
    }
};

const getLogTypeConfig = (type: string) => {
    const t = type?.toLowerCase() || '';
    if (t.includes('login') || t.includes('logged in')) {
        return {
            color: 'text-emerald-700',
            bg: 'bg-emerald-50',
            border: 'border-emerald-200',
            icon: LogIn,
            dot: 'bg-emerald-500'
        };
    }
    if (t.includes('logout') || t.includes('logged out')) {
        return {
            color: 'text-slate-600',
            bg: 'bg-slate-100',
            border: 'border-slate-200',
            icon: LogOut,
            dot: 'bg-slate-400'
        };
    }
    if (t.includes('alarm') || t.includes('error') || t.includes('fail') || t.includes('warn') || t.includes('tamper')) {
        return {
            color: 'text-amber-700',
            bg: 'bg-amber-50',
            border: 'border-amber-200',
            icon: AlertCircle,
            dot: 'bg-amber-500'
        };
    }
    return {
        color: 'text-[#422568]',
        bg: 'bg-[#422568]/10',
        border: 'border-[#422568]/20',
        icon: FileText,
        dot: 'bg-[#422568]'
    };
};

// Format log_time directly without timezone / UTC shift
const formatLogTime = (ts: string) => {
    if (!ts) return '-';
    try {
        const cleanTs = ts.replace('T', ' ').split('.')[0];
        const [datePart, timePart] = cleanTs.split(' ');
        if (!datePart || !timePart) return ts;

        const [year, month, day] = datePart.split('-').map(Number);
        const [hourStr, minStr, secStr] = timePart.split(':');
        const hours = Number(hourStr);
        const minutes = minStr || '00';
        const seconds = secStr || '00';

        if (isNaN(year) || isNaN(month) || isNaN(day) || isNaN(hours)) return ts;

        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const monthName = months[month - 1] || month;

        const ampm = hours >= 12 ? 'PM' : 'AM';
        const formattedHours = hours % 12 || 12;
        const pad = (n: number | string) => String(n).padStart(2, '0');

        return `${monthName} ${day}, ${year}, ${pad(formattedHours)}:${pad(minutes)}:${pad(seconds)} ${ampm}`;
    } catch {
        return ts;
    }
};

// Convert UTC timestamps (like created / updated) to user's local timezone
const formatUtcTimestamp = (ts: string) => {
    if (!ts) return '-';
    try {
        const date = new Date(ts.endsWith('Z') || ts.includes('+') ? ts : `${ts}Z`);
        if (isNaN(date.getTime())) return ts;
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        });
    } catch {
        return ts;
    }
};


interface NVRLogsTableProps {
    clientId?: number;
}

const NVRLogsTable: React.FC<NVRLogsTableProps> = ({ clientId }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const level = useClientLevel();

    // State
    const [logs, setLogs] = useState<NVRLogItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [selectedLog, setSelectedLog] = useState<NVRLogItem | null>(null);
    const [copiedDetail, setCopiedDetail] = useState(false);

    // Pagination - default limit 50 as requested
    const [currentPage, setCurrentPage] = useState<number>(() => {
        const p = searchParams.get('page');
        return p ? parseInt(p, 10) : 1;
    });
    const [itemsPerPage, setItemsPerPage] = useState<number>(() => {
        const l = searchParams.get('limit');
        return l ? parseInt(l, 10) : 50;
    });

    // Sorting
    const [sortBy, setSortBy] = useState<string>(() => searchParams.get('sort_by') || 'log_time');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(() => (searchParams.get('sort_order') as 'asc' | 'desc') || 'desc');

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    // Sync from URL params
    useEffect(() => {
        const p = searchParams.get('page');
        const l = searchParams.get('limit');
        const sb = searchParams.get('sort_by');
        const so = searchParams.get('sort_order');

        if (p) {
            const pageNum = parseInt(p, 10);
            if (!isNaN(pageNum) && pageNum !== currentPage) setCurrentPage(pageNum);
        }
        if (l) {
            const limitNum = parseInt(l, 10);
            if (!isNaN(limitNum) && limitNum !== itemsPerPage) setItemsPerPage(limitNum);
        }
        if (sb && sb !== sortBy) setSortBy(sb);
        if (so && (so === 'asc' || so === 'desc') && so !== sortOrder) setSortOrder(so as 'asc' | 'desc');
    }, [searchParams]);

    // Fetch NVR Logs API
    const fetchLogs = useCallback(async (skip: number = 0, limit: number = itemsPerPage) => {
        try {
            setLoading(true);
            const queryObj: any = {};
            const activeClientId = clientId || level?.id;
            if (activeClientId) {
                queryObj.client_id = activeClientId;
            }

            const params: any = {
                skip,
                limit,
                sort_by: sortBy,
                sort_order: sortOrder,
                query: JSON.stringify(queryObj)
            };

            if (activeClientId) {
                params.client_id = activeClientId;
            }

            const response = await baseapi.get('/nvr_logs', { params });

            if (response.data.status) {
                const logsList = response.data.data || [];
                const total = typeof response.data.total === 'number' ? response.data.total : logsList.length;
                setLogs(logsList);
                setTotalItems(total);
                setTotalPages(Math.ceil(total / limit));
            } else {
                setLogs([]);
                setTotalItems(0);
                setTotalPages(0);
            }
        } catch (error: any) {
            console.error('Error fetching NVR logs:', error);
            showError(error?.response?.data?.message || error.message || 'Failed to fetch NVR logs');
            setLogs([]);
            setTotalItems(0);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    }, [clientId, level, itemsPerPage, sortBy, sortOrder]);

    useEffect(() => {
        fetchLogs((currentPage - 1) * itemsPerPage, itemsPerPage);
    }, [currentPage, itemsPerPage, fetchLogs]);

    const handleRefresh = () => {
        fetchLogs((currentPage - 1) * itemsPerPage, itemsPerPage);
    };

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                next.set('page', String(page));
                return next;
            }, { replace: true });
        }
    };

    const handleItemsPerPageChange = (value: number) => {
        setItemsPerPage(value);
        setCurrentPage(1);
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('limit', String(value));
            next.set('page', '1');
            return next;
        }, { replace: true });
    };

    const handleSort = (field: string) => {
        let nextOrder: 'asc' | 'desc' = 'asc';
        if (sortBy === field) {
            nextOrder = sortOrder === 'asc' ? 'desc' : 'asc';
        }
        setSortBy(field);
        setSortOrder(nextOrder);
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('sort_by', field);
            next.set('sort_order', nextOrder);
            return next;
        }, { replace: true });
    };

    const handleCopyDetails = (detailObj: Record<string, any>) => {
        navigator.clipboard.writeText(JSON.stringify(detailObj, null, 2));
        setCopiedDetail(true);
        setTimeout(() => setCopiedDetail(false), 2000);
    };

    const SortIcon = ({ field }: { field: string }) => {
        if (sortBy !== field) return <ArrowUpDown className="w-2.5 h-2.5 ml-1 text-slate-400 transition-opacity" />;
        return sortOrder === 'asc'
            ? <ArrowUp className="w-2.5 h-2.5 ml-1 text-[#422568]" />
            : <ArrowDown className="w-2.5 h-2.5 ml-1 text-[#422568]" />;
    };

    return (
        <div className="bg-white p-3 pb-20">
            <div className="w-full mx-auto">
                {/* Top Header Row */}
                <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2">
                        <div className="bg-[#422568] p-2 rounded-lg shadow-sm">
                            <HardDrive className="w-4 h-4 text-white" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="font-bold text-lg text-slate-800 leading-none">NVR Logs</h1>
                                {!loading && (
                                    <span className="text-xs font-extrabold text-[#422568] bg-[#422568]/10 px-2 py-0.5 rounded-full">
                                        {totalItems} records
                                    </span>
                                )}
                            </div>
                            <p className="text-xs font-semibold text-slate-500 mt-1">Audit and activity history from NVR devices</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleRefresh}
                            disabled={loading}
                            className="rounded-lg bg-white border border-slate-200 text-slate-600 p-2 hover:bg-slate-50 transition-colors shadow-sm disabled:opacity-50"
                            title="Refresh Logs"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>

                {/* Table Container */}
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs border-collapse">
                            <thead className="bg-slate-50 sticky top-0 z-10">
                                <tr className="border-b border-slate-200">
                                    {/* ID Column */}
                                    <th
                                        className="px-3 py-3 text-[10px] font-black text-slate-800 uppercase tracking-widest cursor-pointer group hover:bg-slate-100 transition-colors w-16"
                                        onClick={() => handleSort('id')}
                                    >
                                        <div className="flex items-center gap-1.5">
                                            <span>#</span>
                                            <SortIcon field="id" />
                                        </div>
                                    </th>

                                    {/* Log Time */}
                                    <th
                                        className="px-3 py-3 text-[10px] font-black text-slate-800 uppercase tracking-widest cursor-pointer group hover:bg-slate-100 transition-colors"
                                        onClick={() => handleSort('log_time')}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Clock size={14} className="text-cyan-600" />
                                            Log Time
                                            <SortIcon field="log_time" />
                                        </div>
                                    </th>

                                    {/* Log Type */}
                                    <th
                                        className="px-3 py-3 text-[10px] font-black text-slate-800 uppercase tracking-widest cursor-pointer group hover:bg-slate-100 transition-colors"
                                        onClick={() => handleSort('log_type')}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Tag size={14} className="text-purple-600" />
                                            Type / Event
                                            <SortIcon field="log_type" />
                                        </div>
                                    </th>

                                    {/* User */}
                                    <th
                                        className="px-3 py-3 text-[10px] font-black text-slate-800 uppercase tracking-widest cursor-pointer group hover:bg-slate-100 transition-colors"
                                        onClick={() => handleSort('log_user')}
                                    >
                                        <div className="flex items-center gap-2">
                                            <User size={14} className="text-emerald-600" />
                                            User
                                            <SortIcon field="log_user" />
                                        </div>
                                    </th>

                                    {/* Detail Summary */}
                                    <th className="px-3 py-3 text-[10px] font-black text-slate-800 uppercase tracking-widest">
                                        <div className="flex items-center gap-2">
                                            <Terminal size={14} className="text-indigo-600" />
                                            Details / Metadata
                                        </div>
                                    </th>

                                    {/* Created At */}
                                    <th
                                        className="px-3 py-3 text-[10px] font-black text-slate-800 uppercase tracking-widest cursor-pointer group hover:bg-slate-100 transition-colors"
                                        onClick={() => handleSort('created')}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Calendar size={14} className="text-yellow-600" />
                                            Recorded At
                                            <SortIcon field="created" />
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {loading ? (
                                    <tr>
                                        <td colSpan={6} className="py-16 text-center text-slate-500 font-bold uppercase tracking-widest italic">
                                            <div className="flex items-center justify-center gap-2">
                                                <Loader2 className="w-4 h-4 animate-spin text-[#422568]" />
                                                Loading NVR logs...
                                            </div>
                                        </td>
                                    </tr>
                                ) : logs.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="py-16 text-center text-slate-500 font-bold uppercase tracking-widest italic">
                                            <div className="flex flex-col items-center gap-2">
                                                <HardDrive className="w-6 h-6 text-slate-300" />
                                                No NVR logs found.
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    logs.map((log) => {
                                        const typeConfig = getLogTypeConfig(log.log_type);
                                        const TypeIcon = typeConfig.icon;
                                        const parsedDetails = parseLogDetail(log.detail);
                                        const ipAddress = parsedDetails['IP Address'] || parsedDetails['ip'] || parsedDetails['ip_address'];
                                        const groupName = parsedDetails['Group Name'] || parsedDetails['group'] || parsedDetails['group_name'];

                                        return (
                                            <tr
                                                key={log.id}
                                                onClick={() => setSelectedLog(log)}
                                                className="hover:bg-slate-50 transition-colors group cursor-pointer border-b border-slate-50"
                                            >
                                                {/* ID */}
                                                <td className="px-3 py-3 font-mono text-[11px] font-bold text-slate-500">
                                                    #{log.id}
                                                </td>

                                                {/* Log Time */}
                                                <td className="px-3 py-3">
                                                    <span className="text-slate-800 text-[11px] font-mono font-bold flex items-center gap-1.5">
                                                        <Clock className="w-3 h-3 text-slate-400" />
                                                        {formatLogTime(log.log_time)}
                                                    </span>
                                                </td>

                                                {/* Log Type */}
                                                <td className="px-3 py-3">
                                                    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold ${typeConfig.bg} ${typeConfig.color} border ${typeConfig.border} shadow-xs`}>
                                                        <TypeIcon className="w-3 h-3" />
                                                        <span>{log.log_type || 'Unknown'}</span>
                                                    </div>
                                                </td>

                                                {/* Log User */}
                                                <td className="px-3 py-3">
                                                    <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-slate-100 text-slate-800 text-[11px] font-bold border border-slate-200">
                                                        <User className="w-3 h-3 text-slate-500" />
                                                        <span>{log.log_user || '-'}</span>
                                                    </div>
                                                </td>

                                                {/* Detail preview */}
                                                <td className="px-3 py-3">
                                                    <div className="flex items-center gap-1.5 flex-wrap max-w-md">
                                                        {ipAddress && (
                                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-mono font-bold border border-blue-100">
                                                                IP: {ipAddress}
                                                            </span>
                                                        )}
                                                        {groupName && (
                                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-purple-50 text-purple-700 text-[10px] font-bold border border-purple-100">
                                                                Group: {groupName}
                                                            </span>
                                                        )}
                                                        {!ipAddress && !groupName && (
                                                            <span className="text-slate-600 text-[11px] font-medium truncate max-w-xs block" title={typeof log.detail === 'string' ? log.detail : JSON.stringify(log.detail)}>
                                                                {typeof log.detail === 'string' ? log.detail : JSON.stringify(log.detail)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>

                                                {/* Created At */}
                                                <td className="px-3 py-3">
                                                    <span className="text-slate-600 text-[10px] font-mono">
                                                        {formatUtcTimestamp(log.created)}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Bar - Matching EventsTable.tsx */}
                    <div className="bg-slate-50/50 border-t border-slate-200 px-6 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <ItemsPerPageDropdown value={itemsPerPage} onChange={handleItemsPerPageChange} />
                            <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">
                                <span className="text-slate-800">{totalItems > 0 ? startIndex + 1 : 0}</span> - <span className="text-slate-800">{Math.min(endIndex, totalItems)}</span> OF {totalItems}
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1 || loading}
                                className="p-1.5 text-slate-400 hover:text-slate-800 disabled:opacity-30 transition-colors"
                                title="First Page"
                            >
                                <ChevronsLeft className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1 || loading}
                                className="p-1.5 text-slate-400 hover:text-slate-800 disabled:opacity-30 transition-colors"
                                title="Previous Page"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <div className="px-3 py-1 bg-[#422568] rounded-lg text-white text-[11px] font-extrabold mx-1.5 shadow-sm">
                                {currentPage}
                            </div>
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages || totalPages === 0 || loading}
                                className="p-1.5 text-slate-400 hover:text-slate-800 disabled:opacity-30 transition-colors"
                                title="Next Page"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages || totalPages === 0 || loading}
                                className="p-1.5 text-slate-400 hover:text-slate-800 disabled:opacity-30 transition-colors"
                                title="Last Page"
                            >
                                <ChevronsRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Log Detail Slide-out Panel */}
            <AnimatePresence>
                {selectedLog && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setSelectedLog(null)}
                            className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-40"
                        />
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                            className="fixed right-0 top-0 h-full w-[480px] bg-white border-l border-slate-200 z-50 flex flex-col shadow-2xl"
                        >
                            {/* Panel Header */}
                            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                                <div className="flex items-center gap-2.5">
                                    <div className="p-2 rounded-xl bg-[#422568]/10 text-[#422568] shadow-xs">
                                        <HardDrive className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <p className="text-xs font-black text-slate-800 leading-tight">
                                            NVR Log #{selectedLog.id}
                                        </p>
                                        <p className="text-[10px] text-slate-500 font-mono font-bold tracking-tight mt-0.5">
                                            Client #{selectedLog.client_id}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setSelectedLog(null)}
                                        className="p-1.5 rounded-lg text-slate-400 hover:text-slate-800 hover:bg-slate-100 transition-all font-bold"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Panel Content */}
                            <div className="flex-1 min-h-0 overflow-y-auto custom-scroll p-5 flex flex-col gap-4 pb-20">
                                {/* Type & Time Banner */}
                                <div className="flex items-center justify-between gap-2 flex-wrap bg-slate-50/70 p-3 rounded-xl border border-slate-100">
                                    <div className="flex items-center gap-2">
                                        {(() => {
                                            const cfg = getLogTypeConfig(selectedLog.log_type);
                                            const Icon = cfg.icon;
                                            return (
                                                <span className={`inline-flex items-center gap-1.5 text-[10px] font-black px-2.5 py-1 rounded-md border uppercase tracking-wider shadow-xs ${cfg.bg} ${cfg.color} ${cfg.border}`}>
                                                    <Icon className="w-3 h-3" />
                                                    {selectedLog.log_type}
                                                </span>
                                            );
                                        })()}
                                    </div>
                                    <span className="text-[10px] text-slate-500 font-mono font-bold flex items-center gap-1">
                                        <Clock className="w-3 h-3 text-slate-400" />
                                        {formatLogTime(selectedLog.log_time)}
                                    </span>
                                </div>

                                {/* Overview Grid */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="rounded-xl border border-slate-100 bg-white p-3 shadow-xs">
                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.15em] mb-1 flex items-center gap-1.5">
                                            <User className="w-3 h-3 text-emerald-600" />
                                            User
                                        </p>
                                        <p className="text-xs text-slate-800 font-bold">{selectedLog.log_user || '-'}</p>
                                    </div>

                                    <div className="rounded-xl border border-slate-100 bg-white p-3 shadow-xs col-span-2">
                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.15em] mb-1 flex items-center gap-1.5">
                                            <Calendar className="w-3 h-3 text-yellow-600" />
                                            Recorded At
                                        </p>
                                        <p className="text-[11px] text-slate-700 font-mono font-bold">{formatUtcTimestamp(selectedLog.created)}</p>
                                    </div>
                                </div>

                                {/* Parsed Detail Section */}
                                {(() => {
                                    const details = parseLogDetail(selectedLog.detail);
                                    const entries = Object.entries(details);

                                    return (
                                        <div className="rounded-xl border border-slate-100 bg-slate-50/30 p-4">
                                            <div className="flex items-center justify-between mb-3 border-b border-slate-100 pb-2">
                                                <p className="text-[10px] font-black text-slate-800 uppercase tracking-[0.2em] flex items-center gap-2">
                                                    <Terminal className="w-3.5 h-3.5 text-[#422568]" />
                                                 Details
                                                </p>
                                                <button
                                                    type="button"
                                                    onClick={() => handleCopyDetails(details)}
                                                    className="flex items-center gap-1 text-[10px] font-bold text-slate-500 hover:text-[#422568] transition-colors"
                                                    title="Copy JSON"
                                                >
                                                    {copiedDetail ? (
                                                        <>
                                                            <Check className="w-3 h-3 text-emerald-600" />
                                                            <span className="text-emerald-600">Copied</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Copy className="w-3 h-3" />
                                                            <span>Copy</span>
                                                        </>
                                                    )}
                                                </button>
                                            </div>

                                            {entries.length > 0 ? (
                                                <div className="grid grid-cols-1 gap-2">
                                                    {entries.map(([k, v]) => (
                                                        <div key={k} className="bg-white p-2.5 rounded-lg border border-slate-100 flex items-start justify-between gap-4 shadow-2xs">
                                                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-tight flex-shrink-0">
                                                                {k}
                                                            </span>
                                                            <span className="text-[11px] font-mono font-bold text-slate-800 text-right break-all">
                                                                {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-xs text-slate-400 italic">No structured details available.</p>
                                            )}

                                            {/* Raw JSON Accordion / Box */}
                                         
                                        
                                        </div>
                                    );
                                })()}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
};

export default NVRLogsTable;
